import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsAdmin } from "../../hooks/useFeatureFlags";
import { UserMenu } from "./TopBar";
import { useAuth } from "../auth/AuthContext";
import { useLicense } from "../../licensing/LicenseContext";
import { getUpdateStatus } from "../../api/updateApi";

const QA_KEY = "quickAccess_pins";

function loadPins() {
  try {
    const raw = localStorage.getItem(QA_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistPins(pins) {
  try {
    localStorage.setItem(QA_KEY, JSON.stringify(pins));
  } catch {}
}

// Global navigation strip: menu icon + back/forward + breadcrumb + quick access bar
const CubeIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M16 3L29 10V12L16 19L3 12V10L16 3Z" fill="#7BAEFF"/>
    <path d="M3 12V22L16 29V19L3 12Z" fill="#1A4DB0"/>
    <path d="M29 12V22L16 29V19L29 12Z" fill="#3570E8"/>
    <path d="M16 3L29 10L16 17L3 10L16 3Z" fill="#90BBFF" opacity="0.6"/>
  </svg>
);

const FlowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="4" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="14" cy="4" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="14" cy="14" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6.4 8.2L11.6 4.8M6.4 9.8L11.6 13.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const RequestIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M15 4v5h5" />
    <path d="M8 13h6" />
    <path d="M8 17h4" />
  </svg>
);

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M9 11V3M6 6L9 3L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 13V15H15V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const LogsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2.5" y="2.5" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 6H13M5 9H10M5 12H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const DocsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M4 2.5H11.5L14.5 5.5V15.5H4V2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M11 2.5V6H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M6.5 9H11.5M6.5 12H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const AdminIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9 1.5V3M9 15V16.5M1.5 9H3M15 9H16.5M3.6 3.6L4.67 4.67M13.33 13.33L14.4 14.4M3.6 14.4L4.67 13.33M13.33 4.67L14.4 3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { hasFeature } = useLicense();
  const { user, token } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);

  const hour = new Date().getHours();
  const timeGreeting =
    hour < 12
      ? t("nav.goodMorning", { defaultValue: "Good morning," })
      : hour < 17
      ? t("nav.goodAfternoon", { defaultValue: "Good afternoon," })
      : t("nav.goodEvening", { defaultValue: "Good evening," });

  const [pins, setPins] = useState(loadPins);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    getUpdateStatus(token)
      .then((info) => setAppVersion(info?.currentVersion ?? null))
      .catch(() => {});
  }, [token]);

  const path = location.pathname || "/";

  const labelForPath = (fullPath, segment) => {
    switch (fullPath) {
      case "/":
        return t("nav.home", { defaultValue: "Home" });
      case "/search-builder":
        return t("nav.searchBuilder", { defaultValue: "Search Builder" });
      case "/flow-designer":
        return t("nav.flowDesigner", { defaultValue: "Flow Designer" });
      case "/flow-designer-iso":
        return t("nav.flowDesignerIso", { defaultValue: "Flow Designer ISO" });
      case "/flow-runner":
        return t("nav.flowRunner", { defaultValue: "Flow Runner" });
      case "/system-logs":
        return t("nav.systemLogs", { defaultValue: "System Logs" });
      case "/upload":
        return t("nav.upload", { defaultValue: "Upload" });
      case "/documentation":
        return t("nav.documentation", { defaultValue: "Documentation" });
      case "/admin":
        return t("nav.admin", { defaultValue: "Admin" });
      case "/admin/org-users":
        return t("nav.orgUsers", { defaultValue: "Users & Groups" });
      case "/admin/indexes":
        return t("nav.adminIndexes", { defaultValue: "Indexes" });
      case "/admin/batches":
        return t("nav.batches", { defaultValue: "Batches" });
      case "/admin/workers":
        return t("nav.workers", { defaultValue: "Workers" });
      case "/admin/audit-logs":
        return t("nav.auditLogs", { defaultValue: "Audit Logs" });
      case "/admin/content-access-logs":
        return t("logs.contentTitle", { defaultValue: "Content Access" });
      case "/admin/legal-holds":
        return t("holds.title", { defaultValue: "Legal Holds" });
      case "/admin/disposition":
        return t("disposition.title", { defaultValue: "Defensible Disposition" });
      case "/admin/classification":
        return t("classification.title", { defaultValue: "Content Classification" });
      case "/admin/content-organization":
        return t("contentOrg.title", { defaultValue: "Content Organization" });
      case "/admin/records-management":
        return t("recordsManagement.title", { defaultValue: "Records Management" });
      case "/admin/workflow":
        return t("workflow.title", { defaultValue: "Workflow" });
      case "/my-tasks":
        return t("workflow.myTasks.title", { defaultValue: "My Tasks" });
      case "/shared-with-me":
        return t("share.inboxTitle", { defaultValue: "Shared with me" });
      case "/document-requests":
        return t("documentRequests.title", { defaultValue: "Document requests" });
      case "/admin/ediscovery":
        return t("eDiscovery.title", { defaultValue: "eDiscovery" });
      case "/admin/delivery":
        return t("delivery.title", { defaultValue: "Delivery & self-service portal" });
      case "/admin/access-control":
        return t("accessControl.title", { defaultValue: "Access Control" });
      case "/admin/access-review":
        return t("accessReview.title", { defaultValue: "Access Reviews" });
      case "/admin/lifecycle":
        return t("lifecycleEvents.backToRules", { defaultValue: "Lifecycle Management" });
      case "/admin/lifecycle-events":
        return t("lifecycleEvents.title", { defaultValue: "Lifecycle Events" });
      case "/admin/license":
        return t("nav.license", { defaultValue: "License" });
      case "/admin/storage-config":
        return t("nav.storageConfig", { defaultValue: "Storage" });
      case "/admin/schemas":
        return t("nav.schemas", { defaultValue: "Schemas" });
      case "/admin/hosts":
        return t("nav.hosts", { defaultValue: "Hosts" });
      default:
        return segment
          .replace(/[-_]/g, " ")
          .replace(/^./, (c) => c.toUpperCase());
    }
  };

  // Pages that are top-level routes but conceptually live under another section.
  // Used so the breadcrumb shows e.g. Home / Admin / System Logs even though
  // the URL is /system-logs (not /admin/system-logs).
  const parentPath = {
    "/system-logs": "/admin",
    "/flow-designer": "/admin",
  };

  const buildCrumbs = (leafPath) => {
    const list = [];
    const seen = new Set();
    let cursor = leafPath;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const segs = cursor.split("/").filter(Boolean);
      const lastSeg = segs[segs.length - 1] ?? "";
      list.unshift({ label: labelForPath(cursor, lastSeg), path: cursor });
      if (cursor === "/") break;
      if (parentPath[cursor]) {
        cursor = parentPath[cursor];
      } else {
        segs.pop();
        cursor = segs.length === 0 ? "/" : "/" + segs.join("/");
      }
    }
    return list;
  };

  const crumbs = buildCrumbs(path);

  const currentLabel = crumbs[crumbs.length - 1]?.label || path;
  const isPinned = pins.some((p) => p.path === path);

  const togglePin = () => {
    setPins((prev) => {
      const next = prev.some((p) => p.path === path)
        ? prev.filter((p) => p.path !== path)
        : [...prev, { path, label: currentLabel }];
      persistPins(next);
      return next;
    });
  };

  const removePin = (pinPath) => {
    setPins((prev) => {
      const next = prev.filter((p) => p.path !== pinPath);
      persistPins(next);
      return next;
    });
  };

  const onBack = () => navigate(-1);
  const onForward = () => navigate(1);
  const toggleMenu = () => setMenuOpen((o) => !o);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <div className="nav-bar">
        <div className="nav-main">
          <div className="nav-menu" ref={menuRef}>
            <button
              type="button"
              className="nav-menu-button"
              onClick={toggleMenu}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={t("nav.menu", { defaultValue: "App menu" })}
            >
              <img src="/logo.svg?v=2" alt="DIXI Vault" className="logo-icon" />
            </button>
            {menuOpen && (
              <div
                className="user-dropdown app-menu-dropdown"
                role="menu"
                style={{ left: 0, right: "auto", width: 280, top: 44 }}
              >
                {/* Header */}
                <div className="app-menu-header">
                  <div className="app-menu-header-text">
                    <div className="app-menu-greeting">{timeGreeting}</div>
                    <div className="app-menu-username">{user?.name || t("nav.user", { defaultValue: "User" })}</div>
                    {appVersion && (
                      <span className="app-menu-role-badge">
                        v{appVersion}
                      </span>
                    )}
                  </div>
                  <div className="app-menu-header-icon" aria-hidden="true">
                    <CubeIcon size={30} />
                  </div>
                </div>

                {/* Workspaces */}
                <div className="app-menu-section">
                  <div className="app-menu-section-title">
                    {t("menu.section.workspaces", { defaultValue: "Workspaces" })}
                  </div>
                  <Link
                    to="/flow-designer"
                    className="menu-item app-menu-item"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="app-menu-icon"><FlowIcon /></span>
                    <span className="app-menu-item-text">
                      <span className="app-menu-label">{t("menu.flowDesigner")}</span>
                      <span className="app-menu-sub">{t("menu.flowDesignerSub", { defaultValue: "Build and manage workflows" })}</span>
                    </span>
                    <span className="app-menu-chevron"><ChevronRight /></span>
                  </Link>
                  <Link
                    to="/upload"
                    className="menu-item app-menu-item"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="app-menu-icon"><UploadIcon /></span>
                    <span className="app-menu-item-text">
                      <span className="app-menu-label">{t("menu.upload", { defaultValue: "Upload" })}</span>
                      <span className="app-menu-sub">{t("menu.uploadSub", { defaultValue: "Upload documents and data" })}</span>
                    </span>
                    <span className="app-menu-chevron"><ChevronRight /></span>
                  </Link>
                  {hasFeature("ai-retrieval") && (
                    <Link
                      to="/document-requests"
                      className="menu-item app-menu-item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      <span className="app-menu-icon"><RequestIcon /></span>
                      <span className="app-menu-item-text">
                        <span className="app-menu-label">{t("documentRequests.title", { defaultValue: "Document requests" })}</span>
                        <span className="app-menu-sub">{t("documentRequests.menuSub", { defaultValue: "Ask the archive what it has" })}</span>
                      </span>
                      <span className="app-menu-chevron"><ChevronRight /></span>
                    </Link>
                  )}
                </div>

                <div className="menu-sep" />

                {/* System */}
                <div className="app-menu-section">
                  <div className="app-menu-section-title">
                    {t("menu.section.system", { defaultValue: "System" })}
                  </div>
                  <Link
                    to="/system-logs"
                    className="menu-item app-menu-item"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="app-menu-icon"><LogsIcon /></span>
                    <span className="app-menu-item-text">
                      <span className="app-menu-label">{t("menu.systemLogs")}</span>
                      <span className="app-menu-sub">{t("menu.systemLogsSub", { defaultValue: "View activity and audit trails" })}</span>
                    </span>
                    <span className="app-menu-chevron"><ChevronRight /></span>
                  </Link>
                  <Link
                    to="/documentation"
                    className="menu-item app-menu-item"
                    role="menuitem"
                    onClick={() => setMenuOpen(false)}
                  >
                    <span className="app-menu-icon"><DocsIcon /></span>
                    <span className="app-menu-item-text">
                      <span className="app-menu-label">{t("menu.documentation", { defaultValue: "Documentation" })}</span>
                      <span className="app-menu-sub">{t("menu.documentationSub", { defaultValue: "Guides and API reference" })}</span>
                    </span>
                    <span className="app-menu-chevron"><ChevronRight /></span>
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="menu-item app-menu-item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      <span className="app-menu-icon"><AdminIcon /></span>
                      <span className="app-menu-item-text">
                        <span className="app-menu-label">{t("menu.adminIndexes")}</span>
                        <span className="app-menu-sub">{t("menu.adminSub", { defaultValue: "Manage users and settings" })}</span>
                      </span>
                      <span className="app-menu-chevron"><ChevronRight /></span>
                    </Link>
                  )}
                </div>

              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="icon-btn plain"
              onClick={onBack}
              aria-label={t("nav.back", { defaultValue: "Back" })}
            >
              {"\u2190"}
            </button>
            <button
              type="button"
              className="icon-btn plain"
              onClick={onForward}
              aria-label={t("nav.forward", { defaultValue: "Forward" })}
            >
              {"\u2192"}
            </button>
          </div>
          <nav
            aria-label={t("nav.breadcrumb", { defaultValue: "Breadcrumb" })}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#264b9a",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              marginLeft: 4,
            }}
          >
            {crumbs.map((c, i) => (
              <span key={c.path}>
                {i > 0 && (
                  <span style={{ color: "#999", margin: "0 4px" }}>/</span>
                )}
                {i < crumbs.length - 1 ? (
                  <Link to={c.path} className="link-btn" style={{ padding: 0 }}>
                    {c.label}
                  </Link>
                ) : (
                  <span>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="qa-group">
            {pins.length > 0 && (
              <div
                className="quick-access-bar"
                aria-label={t("nav.quickAccess", { defaultValue: "Quick access" })}
              >
                <svg
                  className="qa-bar-icon"
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect x="1" y="2" width="14" height="2.5" rx="1.2" fill="currentColor"/>
                  <rect x="1" y="6.75" width="9" height="2.5" rx="1.2" fill="currentColor"/>
                  <rect x="1" y="11.5" width="11" height="2.5" rx="1.2" fill="currentColor"/>
                </svg>
                {pins.map((pin) => (
                  <span
                    key={pin.path}
                    className={`qa-chip${location.pathname === pin.path ? " active" : ""}`}
                  >
                    <Link to={pin.path} className="qa-chip-link">
                      {pin.label}
                    </Link>
                    <button
                      type="button"
                      className="qa-chip-remove"
                      onClick={() => removePin(pin.path)}
                      aria-label={t("nav.unpinPage", { defaultValue: "Remove from quick access" })}
                      title={t("nav.unpinPage", { defaultValue: "Remove from quick access" })}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              className={`qa-pin-btn${isPinned ? " pinned" : ""}`}
              onClick={togglePin}
              title={
                isPinned
                  ? t("nav.unpinPage", { defaultValue: "Remove from quick access" })
                  : t("nav.pinPage", { defaultValue: "Add to quick access" })
              }
              aria-label={
                isPinned
                  ? t("nav.unpinPage", { defaultValue: "Remove from quick access" })
                  : t("nav.pinPage", { defaultValue: "Add to quick access" })
              }
            >
              {isPinned ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="nav-user-slot">
          <UserMenu />
        </div>
      </div>
    </>
  );
}
